import { Suspense, lazy } from "react";
import { Navigate, Outlet, Route, Routes, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { auth } from "@envirohub/utils";
import { switchLanguage } from "@envirohub/i18n";
import { OnboardingShellLayout, RouteLoader } from "@envirohub/ui";
import { useOnboardingStatus } from "@/api";
import { OnboardingFormProvider } from "@/hooks/useOnboardingForm";
import { resolveOnboardTarget, resolveVendorZone } from "./vendorZones";

// Portal layout is co-located with this guard so it is only imported / lazy-loaded
// when the vendor's org is ACTIVE. In every other zone this module is never referenced.
const VendorLayout = lazy(() => import("@/layouts/VendorLayout"));

const OnboardingWelcomePage = lazy(() => import("@/pages/onboarding/OnboardingWelcomePage"));
const OnboardingCompanyPage = lazy(() => import("@/pages/onboarding/OnboardingCompanyPage"));
const OnboardingContactPage = lazy(() => import("@/pages/onboarding/OnboardingContactPage"));
const OnboardingAboutPage = lazy(() => import("@/pages/onboarding/OnboardingAboutPage"));
const OnboardingServicesPage = lazy(() => import("@/pages/onboarding/OnboardingServicesPage"));
const OnboardingReviewPage = lazy(() => import("@/pages/onboarding/OnboardingReviewPage"));
const OnboardingPendingPage = lazy(() => import("@/pages/onboarding/OnboardingPendingPage"));
const DocumentsPage = lazy(() => import("@/pages/DocumentsPage"));
const CoveragePage = lazy(() => import("@/pages/CoveragePage"));
const AssetsPage = lazy(() => import("@/pages/AssetsPage"));
const SuspendedPage = lazy(() => import("@/pages/status/SuspendedPage"));
const RejectedPage = lazy(() => import("@/pages/status/RejectedPage"));

// ─── Helpers ─────────────────────────────────────────────────────────────────

function LoadingScreen() {
  return <RouteLoader fullScreen />;
}

function MustResetPasswordRedirect() {
  return <Navigate to="/change-password" replace />;
}

// ─── Shell layouts ────────────────────────────────────────────────────────────

function OnboardingShell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return (
    <OnboardingShellLayout
      brandLabel="EnviroHub"
      brandSubtitle={t("vendor.onboarding.shellSubtitle")}
      currentLanguage={i18n.language}
      onLanguageToggle={() => switchLanguage(i18n.language === "en" ? "ar" : "en")}
      onSignOut={() => {
        auth.clearAuth();
        queryClient.clear();
        navigate("/login");
      }}
      signOutLabel={t("auth.logout")}
      footerCopyright={t("vendor.onboarding.footer.copyright", {
        year: new Date().getFullYear(),
      })}
      footerLinks={[
        {
          id: "support",
          label: t("vendor.onboarding.footer.support"),
          href: "mailto:support@envirohub.com",
        },
        { id: "privacy", label: t("vendor.onboarding.footer.privacy"), href: "#" },
        { id: "terms", label: t("vendor.onboarding.footer.terms"), href: "#" },
      ]}
    >
      <Outlet />
    </OnboardingShellLayout>
  );
}

function OnboardingLayoutRoute() {
  return (
    <OnboardingFormProvider>
      <OnboardingShell />
    </OnboardingFormProvider>
  );
}

function StatusShell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return (
    <OnboardingShellLayout
      brandLabel="EnviroHub"
      brandSubtitle={t("vendor.nav.portalLabel")}
      currentLanguage={i18n.language}
      onLanguageToggle={() => switchLanguage(i18n.language === "en" ? "ar" : "en")}
      onSignOut={() => {
        auth.clearAuth();
        queryClient.clear();
        navigate("/login");
      }}
      signOutLabel={t("auth.logout")}
      footerCopyright={t("vendor.onboarding.footer.copyright", {
        year: new Date().getFullYear(),
      })}
      footerLinks={[
        {
          id: "support",
          label: t("vendor.onboarding.footer.support"),
          href: "mailto:support@envirohub.com",
        },
        { id: "privacy", label: t("vendor.onboarding.footer.privacy"), href: "#" },
        { id: "terms", label: t("vendor.onboarding.footer.terms"), href: "#" },
      ]}
    >
      <Outlet />
    </OnboardingShellLayout>
  );
}

function StatusLayoutRoute() {
  return <StatusShell />;
}

// ─── Page suspense wrappers ───────────────────────────────────────────────────

const pageFallback = <RouteLoader />;

function OnboardingWelcomeRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <OnboardingWelcomePage />
    </Suspense>
  );
}

function OnboardingCompanyRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <OnboardingCompanyPage />
    </Suspense>
  );
}

function OnboardingContactRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <OnboardingContactPage />
    </Suspense>
  );
}

function OnboardingAboutRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <OnboardingAboutPage />
    </Suspense>
  );
}

function OnboardingReviewRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <OnboardingReviewPage />
    </Suspense>
  );
}

function OnboardingPendingRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <OnboardingPendingPage />
    </Suspense>
  );
}

function OnboardingDocumentsRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <DocumentsPage showOnboardBackLink />
    </Suspense>
  );
}

function OnboardingServicesRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <OnboardingServicesPage />
    </Suspense>
  );
}

function OnboardingCoverageRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <CoveragePage />
    </Suspense>
  );
}

function OnboardingAssetsRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <AssetsPage />
    </Suspense>
  );
}

function SuspendedStatusRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <SuspendedPage />
    </Suspense>
  );
}

function RejectedStatusRoute() {
  return (
    <Suspense fallback={pageFallback}>
      <RejectedPage />
    </Suspense>
  );
}

// ─── Zone guard ───────────────────────────────────────────────────────────────

/**
 * Single source of truth for vendor routing.
 *
 * - Blocks ALL rendering behind a full-screen loader until the onboarding
 *   status query settles on first load (no cached data).
 * - After settling, renders ONLY the route tree appropriate for the vendor's
 *   org-status zone.
 * - `VendorLayout` (and every portal page / portal query hook) can only mount
 *   when zone === "portal". In every other zone it is completely absent from
 *   the rendered tree.
 */
export function VendorZoneGuard() {
  const { data, isPending, isError, isFetchedAfterMount } = useOnboardingStatus();

  // Wait for a fresh /status response this mount — never unlock the portal from a
  // previous session's cached ACTIVE value.
  if (isPending || !isFetchedAfterMount) {
    return <LoadingScreen />;
  }

  const pendingApproval = data?.latestApprovalRequest?.status === "PENDING";
  const zone = resolveVendorZone(data?.status, isError);
  const onboardTarget = resolveOnboardTarget(pendingApproval);

  // ── Portal zone (ACTIVE) ──────────────────────────────────────────────────
  // Only here does VendorLayout mount. Redirect /onboard/* and /status/* back
  // to portal root in case an ACTIVE vendor has a stale bookmark.
  // If the org just became ACTIVE but the user still must reset their password,
  // send them to change-password before any portal queries fire.
  if (zone === "portal") {
    if (auth.getSession()?.mustResetPassword) {
      return <MustResetPasswordRedirect />;
    }

    return (
      <Routes>
        <Route path="/onboard/*" element={<Navigate to="/" replace />} />
        <Route path="/status/*" element={<Navigate to="/" replace />} />
        <Route
          path="/*"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <VendorLayout />
            </Suspense>
          }
        />
      </Routes>
    );
  }

  // ── Suspended zone ────────────────────────────────────────────────────────
  if (zone === "suspended") {
    return (
      <Routes>
        <Route path="/status" element={<StatusLayoutRoute />}>
          <Route path="suspended" element={<SuspendedStatusRoute />} />
          <Route path="*" element={<Navigate to="/status/suspended" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/status/suspended" replace />} />
      </Routes>
    );
  }

  // ── Rejected zone ─────────────────────────────────────────────────────────
  if (zone === "rejected") {
    return (
      <Routes>
        <Route path="/status" element={<StatusLayoutRoute />}>
          <Route path="rejected" element={<RejectedStatusRoute />} />
          <Route path="*" element={<Navigate to="/status/rejected" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/status/rejected" replace />} />
      </Routes>
    );
  }

  // ── Onboard zone (PENDING_ONBOARDING, CHANGES_REQUESTED, error fallback) ──
  // When the approval request is PENDING, lock the vendor to /onboard/pending
  // so no other wizard step can render. When the status query updates after
  // submit, any remaining wizard step route will no longer match and the
  // catch-all redirects to /onboard/pending.
  return (
    <Routes>
      <Route element={<OnboardingLayoutRoute />}>
        {pendingApproval ? (
          <>
            <Route path="/onboard/pending" element={<OnboardingPendingRoute />} />
            <Route path="/onboard" element={<Navigate to="/onboard/pending" replace />} />
            <Route path="/onboard/*" element={<Navigate to="/onboard/pending" replace />} />
            <Route path="*" element={<Navigate to="/onboard/pending" replace />} />
          </>
        ) : (
          <>
            <Route path="/onboard" element={<OnboardingWelcomeRoute />} />
            <Route path="/onboard/company" element={<OnboardingCompanyRoute />} />
            <Route path="/onboard/contact" element={<OnboardingContactRoute />} />
            <Route path="/onboard/about" element={<OnboardingAboutRoute />} />
            <Route path="/onboard/documents" element={<OnboardingDocumentsRoute />} />
            <Route path="/onboard/services" element={<OnboardingServicesRoute />} />
            <Route
              path="/onboard/services/:vendorServiceId"
              element={<Navigate to="/onboard/services" replace />}
            />
            <Route path="/onboard/review" element={<OnboardingReviewRoute />} />
            <Route path="/onboard/pending" element={<OnboardingPendingRoute />} />
            <Route path="/coverage" element={<OnboardingCoverageRoute />} />
            <Route path="/fleet" element={<OnboardingAssetsRoute />} />
            <Route path="/onboard/*" element={<Navigate to="/onboard" replace />} />
            <Route path="*" element={<Navigate to={onboardTarget} replace />} />
          </>
        )}
      </Route>
    </Routes>
  );
}
